<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceLines',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    currency: {
      type: Object,
      required: true,
    },
    defaultDiscount: {
      type: Number,
      required: false,
    },
    defaultTaxCode: {
      type: String,
      required: false,
    },
    formContext: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onProductSelected = (...args) => {
    let [row, product] = args;

    invoiceLineHelper.value.onProductSelected(row.data, product);
    row.setFocus('quantity');
  };
  const hasQuantityMismatch = (...args) => {
    const row = args[0];
    return (
      row.data.accountings &&
      row.data.accountings.data &&
      row.data.accountings.data.length &&
      _.sumBy(row.data.accountings.data, (p) => p.quantity) != row.data.quantity
    );
  };
  const getInvoiceLineEditorIsDisabled = (...args) => {
    const [row] = args;
    return !row.tax_code;
  };
  const onRentalOrPropertySelected = (...args) => {
    let [row, selection] = args;

    console.log(row, selection);

    if (!selection) {
      row.data.rental = null;
      row.data.property = null;
      return;
    }

    if (_.isObject(selection) && selection.object == 'rental') {
      row.data.rental = selection;
      row.data.property = null;
      return;
    }

    if (
      _.isObject(selection) &&
      ['property', 'property_unit'].indexOf(selection.object) > -1
    ) {
      row.data.rental = null;
      row.data.property = selection;
      return;
    }
  };

  const expenseTypes = ref(null);
  const taxCodes = ref(null);
  const currencies = ref(null);
  const units = ref(null);
  const displayAddInvoiceLine = ref(null);
  const messages = ref({
    invoiceLineProductSearchPlaceholder: $fn.value.pgettext(
      'InvoiceLines',
      'Search product/service...'
    ),
    invoiceLineCostCenterSearchPlaceholder: $fn.value.pgettext(
      'invoiceLines',
      'search...'
    ),
    invoiceLineDescriptionPlaceholder: $fn.value.pgettext(
      'InvoiceLines',
      'Description'
    ),
  });
  const invoiceLineHelper = ref(null);
  const ux = ref([]);
  const b_5962862420439144_rowHeader = computed(() => false);
  const b_5962862420439144_paging = computed(() => false);
  const b_5962862420439144_sortable = computed(() => false);
  const b_5962862420439144_rowTemplate = computed(() => ({
    description: null,
    discount: props.defaultDiscount || 0,
    notes: null,
  }));
  const b_5962862420439144_messages = computed(() => ({
    msgCreateRow: $fn.value.pgettext('InvoiceLines', 'Add line'),
    msgCreateProduct: $fn.value.pgettext('InvoiceLines', 'Add new product...'),
    msgSearchProduct: $fn.value.pgettext(
      'InvoiceLines',
      'Search for product/service...'
    ),
    msgSearchCostCenter: $fn.value.pgettext('InvoiceLines', 'search...'),
  }));
  const b_5962862420439144_columns = computed(() => [
    {
      name: 'product',
      header: $fn.value.gettext('Product'),
      width: '250px',
      required: (row) => !row.id || !row.description,
      textAlign: 'left',
      editor: {
        name: 'template',
        value:
          '<ItpFormSearchableSelect\n\
  class="w-100"\n\
  v-model="currentValue"\n\
  bindObject\n\
  :name="col.field"\n\
  valueField="id"\n\
  textField="name"\n\
  required\n\
  :placeholder="messages.msgSearchProduct"\n\
  :options="globs.searchDataSource(\'suggest_product\', {invoice_line_products: \'include\'})"\n\
  :autofocus=autofocus\n\
  :form="formId"\n\
  :inputId="col.field"\n\
  :size="col.size"\n\
  :state="validationState"\n\
  :disabled="isDisabled"\n\
  :tabindex="col.tabindex"\n\
  taggable\n\
  @optionSelected="col.emit(\'product:selected\', {col, row, ...$event})"\n\
  @change="onChange($event)"\n\
>\n\
  <template #search="{attributes, events}">\n\
    <input\n\
      class="vs__search"\n\
      v-bind="attributes"\n\
      v-on="events"\n\
    />\n\
  </template>\n\
\n\
  <template #option="option">\n\
    <UcSuggestProductOption :option="option" v-if="option.object==\'product\'">\n\
    </UcSuggestProductOption>\n\
    <span v-else>{{ option.name }}</span>\n\
  </template>\n\
\n\
  <template #selected-option="option">\n\
    <UcSuggestProductOption :option="option" v-if="option.object==\'product\'">\n\
    </UcSuggestProductOption>\n\
    <span v-else>{{ option.name }}</span>\n\
  </template>\n\
\n\
  <template #list-footer>\n\
    <hr />\n\
    <ItpButton\n\
      class="ms-2"\n\
      size=sm\n\
      variant=light\n\
      @click="col.emitCellEvent(\'createProduct\', row)">\n\
      {{ messages.msgCreateProduct }}\n\
    </ItpButton>\n\
  </template>\n\
\n\
  <template #footer>\n\
    <BFormInput\n\
      class="mt-1"\n\
      type=text\n\
      v-model=row.description\n\
      id="description"\n\
      name="description"\n\
      autocomplete="off"\n\
      :form="formId"\n\
      :size=col.size\n\
      :disabled="isDisabled"\n\
      :placeholder="globs.messages.invoiceLineDescriptionPlaceholder"\n\
      @change="onFieldChange(\'description\', row.description)"\n\
    ></BFormInput>\n\
  </template>\n\
</ItpFormSearchableSelect>\n\
',
      },
      formatter: {
        name: 'template',
        value:
          '<div class="text-truncate" :title="row.description">\n\
  <BLink v-if="row.product && row.product.id" class="fw-bold" :to="{\n\
    name: \'app.product\',\n\
    params: { id: row.product.id }\n\
  }">{{ row.description }}</BLink>\n\
  <span v-if="!row.product">\n\
    {{ row.description }}\n\
  </span>\n\
</div>\n\
',
      },
    },
    {
      name: 'advance',
      header: $fn.value.pgettext('InvoiceLine|ColHeader', 'Adv.'),
      headerTooltip: $fn.value.pgettext(
        'invoiceLine|ColHeader|Tooltip',
        'Advance'
      ),
      headerTextAlign: 'center',
      width: '25px',
      type: 'checkbox',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'intermediated_service',
      header: $fn.value.pgettext('AbbrIntermediatedService', 'IMS'),
      headerTooltip: $fn.value.gettext('Intermediated Service'),
      headerTextAlign: 'center',
      width: '25px',
      type: 'checkbox',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'quantity',
      type: 'number',
      maxAllowedDecimals: isIncomingInvoice.value
        ? null
        : props.currency.quantity_digits,
      default: 0,
      header: $fn.value.pgettext('Quantity Abbr', 'Qty'),
      headerTooltip: $fn.value.gettext('Quantity'),
      headerTextAlign: 'right',
      width: '60px',
      textAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'unit',
      header: $fn.value.pgettext('Unit Abbr', 'Unit'),
      headerTooltip: $fn.value.gettext('Unit'),
      width: '70px',
      textAlign: 'center',
      headerTextAlign: 'center',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'price',
      type: 'number',
      maxAllowedDecimals: isIncomingInvoice.value
        ? null
        : props.currency.price_digits,
      default: 0,
      header: $fn.value.gettext('Price'),
      width: '70px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
      footerTotals: (rows) => _.meanBy(rows, (p) => p.quantity * p.price),
    },
    {
      name: 'discount',
      type: 'number',
      maxAllowedDecimals: isIncomingInvoice.value
        ? null
        : props.currency.digits,
      default: props.defaultDiscount || 0,
      header: $fn.value.pgettext('AbbrDiscount', 'Disc'),
      headerTooltip: $fn.value.gettext('Discount'),
      noFooterTotals: true,
      width: '50px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'net_amount',
      type: 'number',
      maxAllowedDecimals: props.currency.digits,
      default: 0,
      header: $fn.value.gettext('Net'),
      width: '95px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'amount',
      type: 'number',
      maxAllowedDecimals: props.currency.digits,
      default: 0,
      header: $fn.value.gettext('Amount'),
      width: '95px',
      textAlign: 'right',
      headerTextAlign: 'right',
      required: true,
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'tax_code',
      header: $fn.value.gettext('VAT'),
      headerTooltip: $fn.value.gettext('VAT Code'),
      width: '80px',
      type: 'select',
      options: taxCodes.value,
      valueField: 'id',
      textField: 'id',
      textAlign: 'right',
      headerTextAlign: 'right',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'deductible_vat_amount',
      type: 'number',
      disabled: isOutgoingInvoice.value,
      header: $fn.value.gettext('D-VAT'),
      headerTooltip: $fn.value.gettext('Deductible VAT amount'),
      maxAllowedDecimals: props.currency.digits,
      default: 0,
      width: '95px',
      textAlign: 'right',
      headerTextAlign: 'right',
      editorIsDisabled: getInvoiceLineEditorIsDisabled,
    },
    {
      name: 'expense_type',
      type: 'select',
      header: $fn.value.pgettext('InvoiceLine|ColHeader', 'Expense Type'),
      width: '100px',
      options: expenseTypes.value,
      valueField: 'name',
      textField: 'name',
    },
    {
      name: 'rental_or_property',
      header: $fn.value.gettext('Cost Center'),
      textAlign: 'left',
      width: '100px',
      editor: {
        name: 'template',
        value:
          '<ItpFormSearchableSelect\n\
  class="w-100"\n\
  v-model="currentValue"\n\
  bindObject\n\
  :name="col.field"\n\
  valueField="id"\n\
  textField="name"\n\
  required\n\
  :placeholder="messages.msgSearchCostCenter"\n\
  :options="globs.searchDataSource(\'suggest_cost_center\')"\n\
  :initialOptions="[row.rental || row.property]"\n\
  :form="formId"\n\
  :inputId="col.field"\n\
  :size="col.size"\n\
  :state="validationState"\n\
  :disabled="isDisabled"\n\
  :tabindex="col.tabindex"\n\
  @optionSelected="col.emit(\'rental_or_property:selected\', {col, row, ...$event})"\n\
  @change="onChange($event)"\n\
>\n\
  <template #search="{attributes, events}">\n\
    <input\n\
      class="vs__search"\n\
      v-bind="attributes"\n\
      v-on="events"\n\
    />\n\
  </template>\n\
\n\
  <template #option="option">\n\
    <UcSuggestRentalOption :option="option" v-if="option.object==\'rental\'">\n\
    </UcSuggestRentalOption>\n\
    <UcSuggestPropertyOption :option="option" v-if="option.object==\'property\' || option.object == \'property_unit\'">\n\
    </UcSuggestPropertyOption>\n\
  </template>\n\
\n\
  <template #selected-option="option">\n\
    <UcSuggestRentalOption :option="option" v-if="option.object==\'rental\'">\n\
    </UcSuggestRentalOption>\n\
    <UcSuggestPropertyOption :option="option" v-if="option.object==\'property\' || option.object == \'property_unit\'">\n\
    </UcSuggestPropertyOption>\n\
  </template>\n\
</ItpFormSearchableSelect>\n\
',
      },
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <UcSuggestRentalOption :option="row.rental" v-if="row.rental">\n\
  </UcSuggestRentalOption>\n\
  <UcSuggestPropertyOption :option="row.property" v-if="row.property" display-code>\n\
  </UcSuggestPropertyOption>\n\
</div>\n\
',
      },
    },
  ]);
  const b_1270943406604881_dataSource = computed(() => ({
    name: 'suggest_invoice',
    parameters: {
      invoice_kind: props.invoice.invoice_kind,
      client_id: props.invoice.client.id,
      company_id: props.invoice.company.id,
      exclude_invoice: props.invoice.id,
    },
  }));
  const b_1803307708030275_paging = computed(() => false);
  const b_1803307708030275_sortable = computed(() => false);
  const b_1803307708030275_rowHeader = computed(() => false);
  const b_1803307708030275_fillEmptyRowSpace = computed(() => false);
  const b_1803307708030275_messages = computed(() => ({
    msgCreateRow: $fn.value.pgettext('invoiceLines', 'New meter row'),
    msgDate: $fn.value.pgettext('InputPlaceholder', 'Date'),
    msgReading: $fn.value.pgettext('InputPlaceholder', 'Reading'),
    msgUnit: $fn.value.pgettext('InputPlaceholder', 'Unit'),
  }));
  const b_1803307708030275_columns = computed(() => [
    {
      name: 'meter',
      header: $fn.value.gettext('Meter'),
      width: '160px',
      required: true,
      type: 'select2',
      options: $fn.value.searchDataSource('suggest_meter'),
      bindObject: true,
      valueField: 'id',
      textField: 'serial_number',
      formatter: {
        name: 'template',
        value:
          '<UcMeterMiniCard :item="row.meter" link-style-underline no-strong></UcMeterMiniCard>\n\
',
      },
    },
    {
      name: 'from',
      header: $fn.value.gettext('Previous reading'),
      width: '160px',
      formatter: {
        name: 'template',
        value:
          '<div>{{date(row.from_date) }} {{number(row.from_amount) }} {{ row.from_unit }}</div>\n\
',
      },
      editor: {
        name: 'template',
        value:
          '<div class="input-group">\n\
  <ItpFormDatetime\n\
    v-model="row.from_date"\n\
    name="from_date"\n\
    :placeholder="messages.msgDate"\n\
    size="sm"\n\
    style="min-width:100px;"\n\
    required\n\
  ></ItpFormDatetime>\n\
\n\
  <ItpFormInput\n\
    mask="number"\n\
    unmask="typed"\n\
    scale="any"\n\
    name="from_amount"\n\
    v-model="row.from_amount"\n\
    :placeholder="messages.msgReading"\n\
    size="sm"\n\
    style="min-width:60px;"\n\
    required\n\
  ></ItpFormInput>\n\
\n\
  <ItpFormNativeSelect\n\
    v-model="row.from_unit"\n\
    name="from_unit"\n\
    :placeholder="messages.msgUnit"\n\
    size="sm"\n\
    :options="globs.units"\n\
    style="min-width:60px;"\n\
    valueField="symbol"\n\
    textField="symbol"\n\
    required\n\
  ></ItpFormNativeSelect>\n\
</div>\n\
',
      },
    },
    {
      name: 'to',
      header: $fn.value.gettext('Current reading'),
      width: '240px',
      formatter: {
        name: 'template',
        value:
          '<div>{{date(row.to_date) }} {{number(row.to_amount) }} {{ row.to_unit }}</div>\n\
',
      },
      editor: {
        name: 'template',
        value:
          '<div class="input-group">\n\
  <ItpFormDatetime\n\
    v-model="row.to_date"\n\
    name="to_date"\n\
    :placeholder="messages.msgDate"\n\
    size="sm"\n\
    style="min-width:100px;"\n\
    required\n\
  ></ItpFormDatetime>\n\
\n\
  <ItpFormInput\n\
    mask="number"\n\
    unmask="typed"\n\
    scale="any"\n\
    name="to_amount"\n\
    v-model="row.to_amount"\n\
    :placeholder="messages.msgReading"\n\
    size="sm"\n\
    style="min-width:60px;"\n\
    required\n\
  ></ItpFormInput>\n\
\n\
  <ItpFormNativeSelect\n\
    v-model="row.to_unit"\n\
    name="to_unit"\n\
    :placeholder="messages.msgUnit"\n\
    size="sm"\n\
    :options="globs.units"\n\
    style="min-width:60px;"\n\
    valueField="symbol"\n\
    textField="symbol"\n\
    required\n\
  ></ItpFormNativeSelect>\n\
</div>\n\
',
      },
    },
    {
      name: 'consumption',
      header: $fn.value.gettext('Consumption'),
      readonly: true,
      formatter: {
        name: 'template',
        value:
          '<div>{{number(row.consumption_amount) }} {{ row.consumption_unit }}</div>\n\
',
      },
    },
    {
      name: 'billing_multiplier',
      header: $fn.value.gettext('Multiplier'),
      default: 1,
      width: '70px',
      type: 'number',
      required: true,
    },
  ]);
  const b_7700024326226090_paging = computed(() => false);
  const b_7700024326226090_sortable = computed(() => false);
  const b_7700024326226090_rowHeader = computed(() => false);
  const b_7700024326226090_fillEmptyRowSpace = computed(() => false);
  const b_7700024326226090_messages = computed(() => ({
    msgCreateRow: $fn.value.pgettext('invoiceLines', 'New accounting row'),
  }));
  const b_7700024326226090_columns = computed(() => [
    {
      name: 'expense_type',
      type: 'select',
      header: $fn.value.gettext('Expense Type'),
      width: '100px',
      options: expenseTypes.value,
      valueField: 'name',
      textField: 'name',
    },
    {
      name: 'rental',
      type: 'select2',
      header: $fn.value.gettext('Rental'),
      width: '100px',
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_rental'),
      useOptionTemplateForSelectedOption: true,
      optionTemplate:
        '<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>',
    },
    {
      name: 'property',
      type: 'select2',
      header: $fn.value.gettext('Property'),
      width: '100px',
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_property'),
      useOptionTemplateForSelectedOption: true,
      optionTemplate:
        '<UcSuggestPropertyOption :option="option"></UcSuggestPropertyOption>',
    },
    {
      name: 'contractor',
      type: 'select2',
      header: $fn.value.gettext('Contractor'),
      width: '100px',
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_company', {
        role: 'partner',
        'is-contractor': true,
      }),
    },
    {
      name: 'quantity',
      type: 'number',
      header: $fn.value.gettext('Quantity'),
      width: '100px',
    },
  ]);

  const $data = computed(() => ({
    expenseTypes: expenseTypes.value,
    taxCodes: taxCodes.value,
    currencies: currencies.value,
    units: units.value,
    displayAddInvoiceLine: displayAddInvoiceLine.value,
    messages: messages.value,
    invoiceLineHelper: invoiceLineHelper.value,
    ux: ux.value,
    b_5962862420439144_rowHeader: b_5962862420439144_rowHeader.value,
    b_5962862420439144_paging: b_5962862420439144_paging.value,
    b_5962862420439144_sortable: b_5962862420439144_sortable.value,
    b_5962862420439144_rowTemplate: b_5962862420439144_rowTemplate.value,
    b_5962862420439144_messages: b_5962862420439144_messages.value,
    b_5962862420439144_columns: b_5962862420439144_columns.value,
    b_1270943406604881_dataSource: b_1270943406604881_dataSource.value,
    b_1803307708030275_paging: b_1803307708030275_paging.value,
    b_1803307708030275_sortable: b_1803307708030275_sortable.value,
    b_1803307708030275_rowHeader: b_1803307708030275_rowHeader.value,
    b_1803307708030275_fillEmptyRowSpace:
      b_1803307708030275_fillEmptyRowSpace.value,
    b_1803307708030275_messages: b_1803307708030275_messages.value,
    b_1803307708030275_columns: b_1803307708030275_columns.value,
    b_7700024326226090_paging: b_7700024326226090_paging.value,
    b_7700024326226090_sortable: b_7700024326226090_sortable.value,
    b_7700024326226090_rowHeader: b_7700024326226090_rowHeader.value,
    b_7700024326226090_fillEmptyRowSpace:
      b_7700024326226090_fillEmptyRowSpace.value,
    b_7700024326226090_messages: b_7700024326226090_messages.value,
    b_7700024326226090_columns: b_7700024326226090_columns.value,
  }));

  const $$load_expenseTypes = () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'expense-type' },
    });
  };
  const $$load_taxCodes = () => {
    return $app.value.repo.get('list_tax_code');
  };
  const $$load_currencies = () => {
    return $app.value.repo.get('list_currency');
  };
  const $$load_units = () => {
    return $app.value.repo.get('list_unit');
  };
  const $$load_invoiceLineHelper = () => {
    return new $app.value.helpers.InvoiceLineHelper(
      props.invoice,
      $data.value.currencies,
      $data.value.taxCodes
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'expenseTypes' },
      { name: 'taxCodes' },
      { name: 'currencies' },
      { name: 'units' },
      { name: 'displayAddInvoiceLine' },
      { name: 'messages' },
      { name: 'invoiceLineHelper', dependsOn: ['currencies', 'taxCodes'] },
      { name: 'ux' },
      { name: 'b_5962862420439144_rowHeader', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_paging', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_sortable', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_rowTemplate', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_messages', nodeId: '5962862420439144' },
      {
        name: 'b_5962862420439144_columns',
        nodeId: '5962862420439144',
        dependsOn: ['expenseTypes', 'taxCodes'],
      },
      { name: 'b_1270943406604881_dataSource', nodeId: '1270943406604881' },
      { name: 'b_1803307708030275_paging', nodeId: '1803307708030275' },
      { name: 'b_1803307708030275_sortable', nodeId: '1803307708030275' },
      { name: 'b_1803307708030275_rowHeader', nodeId: '1803307708030275' },
      {
        name: 'b_1803307708030275_fillEmptyRowSpace',
        nodeId: '1803307708030275',
      },
      { name: 'b_1803307708030275_messages', nodeId: '1803307708030275' },
      { name: 'b_1803307708030275_columns', nodeId: '1803307708030275' },
      { name: 'b_7700024326226090_paging', nodeId: '7700024326226090' },
      { name: 'b_7700024326226090_sortable', nodeId: '7700024326226090' },
      { name: 'b_7700024326226090_rowHeader', nodeId: '7700024326226090' },
      {
        name: 'b_7700024326226090_fillEmptyRowSpace',
        nodeId: '7700024326226090',
      },
      { name: 'b_7700024326226090_messages', nodeId: '7700024326226090' },
      {
        name: 'b_7700024326226090_columns',
        nodeId: '7700024326226090',
        dependsOn: ['expenseTypes'],
      },
    ],
    loaders: {
      expenseTypes: $$load_expenseTypes,
      taxCodes: $$load_taxCodes,
      currencies: $$load_currencies,
      units: $$load_units,
      invoiceLineHelper: $$load_invoiceLineHelper,
    },
    ux: {},
    dataRefs: {
      expenseTypes,
      taxCodes,
      currencies,
      units,
      displayAddInvoiceLine,
      messages,
      invoiceLineHelper,
      ux,
      b_5962862420439144_rowHeader,
      b_5962862420439144_paging,
      b_5962862420439144_sortable,
      b_5962862420439144_rowTemplate,
      b_5962862420439144_messages,
      b_5962862420439144_columns,
      b_1270943406604881_dataSource,
      b_1803307708030275_paging,
      b_1803307708030275_sortable,
      b_1803307708030275_rowHeader,
      b_1803307708030275_fillEmptyRowSpace,
      b_1803307708030275_messages,
      b_1803307708030275_columns,
      b_7700024326226090_paging,
      b_7700024326226090_sortable,
      b_7700024326226090_rowHeader,
      b_7700024326226090_fillEmptyRowSpace,
      b_7700024326226090_messages,
      b_7700024326226090_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const invoiceLines = computed(() => {
    return props.invoice.lines.data;
  });
  const sortedInvoiceLines = computed(() => {
    return _.orderBy(props.invoice.lines.data, [
      (p) => (p.order_index == null ? Infinity : p.order_index),
    ]);
  });
  const invoiceLineGroups = computed(() => {
    return _.orderBy(props.invoice.line_groups.data, [
      (p) => (p.order_index == null ? Infinity : p.order_index),
    ]);
  });
  const isIncomingInvoice = computed(() => {
    return props.invoice.invoice_kind == 'incoming';
  });
  const isOutgoingInvoice = computed(() => {
    return props.invoice.invoice_kind == 'outgoing';
  });
  const hasSelectedInvoiceLines = computed(() => {
    return (
      props.formContext.selectedInvoiceLines &&
      props.formContext.selectedInvoiceLines.length > 0
    );
  });
  const invoiceRentalNumber = computed(() => {
    return _.chain(props.invoice.references.data)
      .find({ object: 'rental' })
      .get('name')
      .value();
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.currency,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('currency');
    }
  );
  watch(
    () => props.defaultDiscount,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('defaultDiscount');
    }
  );
  watch(
    () => props.defaultTaxCode,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('defaultTaxCode');
    }
  );
  watch(
    () => props.formContext,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formContext');
    }
  );

  const act_1889980785424776_showModal_g0f01uaf5_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'UploadInvoiceLines',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_4892266742011185_setData_vaulrp05u_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'formContext.selectedInvoiceLines',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4892266742011185_emit_4s37pgv56_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4892266742011185_request_omq5308ko_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_many_invoice_line',
      data: {
        invoice_id: props.invoice.id,
        items: _.map(props.formContext.selectedInvoiceLines, (p) => ({
          id: p.data.id,
          etag: p.data.etag,
        })),
      },
    };
  };
  const act_5962862420439144_emit_j6peei6yy_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5962862420439144_script_un1t1gnlh_value_callable = async (
    $event
  ) => {
    return props.invoice.id
      ? $event.data.waitFor(
          $fn.value
            .crud('invoice_line')
            .upsert({ ...$event.data.row, invoice_id: props.invoice.id })
        )
      : invoiceLines.value.splice(
          invoiceLines.value.length - 1,
          0,
          $event.data.row
        );
  };
  const act_5962862420439144_script_un1t1gnlh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_un1t1gnlh_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_upqx6xfy7_value_callable = async (
    $event
  ) => {
    return onProductSelected(
      $event.parent.data.row,
      $event.data.modalResult.value
    );
  };
  const act_5962862420439144_script_upqx6xfy7_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_upqx6xfy7_value_callable($event);
      },
    };
  };
  const act_5962862420439144_showModal_vhqp2tn3u_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProduct',
      props: {
        preventDefaultOnClose: true,
      },
    };
  };
  const act_5962862420439144_script_sfkp4ri0t_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line').delete($event.data.row)
    );
  };
  const act_5962862420439144_script_sfkp4ri0t_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_sfkp4ri0t_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_2yeb6ntrk_value_callable = async (
    $event
  ) => {
    return onProductSelected($event.data.row, $event.data.option);
  };
  const act_5962862420439144_script_2yeb6ntrk_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_2yeb6ntrk_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_gek3l0sww_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_gek3l0sww_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_gek3l0sww_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_ekae4xjup_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_ekae4xjup_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_ekae4xjup_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_2oaevpjo8_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_2oaevpjo8_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_2oaevpjo8_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_tmrccs4jr_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_tmrccs4jr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_tmrccs4jr_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_2r25ctv83_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_2r25ctv83_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_2r25ctv83_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_xdypq8nsb_value_callable = async (
    $event
  ) => {
    return invoiceLineHelper.value.recalculateLineItem(
      $event.data.row.data,
      $event.data.field
    );
  };
  const act_5962862420439144_script_xdypq8nsb_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_xdypq8nsb_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_3bd42187a_value_callable = async (
    $event
  ) => {
    return onRentalOrPropertySelected($event.data.row, $event.data.option);
  };
  const act_5962862420439144_script_3bd42187a_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_3bd42187a_value_callable($event);
      },
    };
  };
  const act_5962862420439144_setData_vrhob4hv5_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'formContext.selectedInvoiceLines',
      value: $event.data.selection,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_emit_wipqptb3c_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_309515315410323_emit_6yrbsdxiu_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_309515315410323_close_5umqs2v3j_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_309515315410323_showModal_51mcfaqtg_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceLineGroup',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_880971572885910_script_7n6fdlyut_value_callable = async (
    $event
  ) => {
    return $fn.value
      .crud('invoice_line')
      .upsert({
        id: $event.scope.row.data.id,
        etag: $event.scope.row.data.etag,
        meter_accounting: {
          invoice_id: props.invoice.id,
          invoice_line_id: $event.scope.row.data.id,
          ...$event.data.modalResult.value,
        },
      })
      .then(() => emit('reload'));
  };
  const act_880971572885910_script_7n6fdlyut_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_880971572885910_script_7n6fdlyut_value_callable($event);
      },
    };
  };
  const inst_evh_880971572885910_close_z3wdiomu5_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_880971572885910_showModal_h8gcxm1wh_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceLineMeterReadingSelector',
      props: {
        rental: invoiceRentalNumber.value,
        multiple: false,
        serviceId: $event.scope.row.data.product
          ? $event.scope.row.data.product.id
          : null,
      },
    };
  };
  const act_1803307708030275_script_p2jxl32ur_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line').upsert({
        id: $event.scope.row.data.id,
        etag: $event.scope.row.data.etag,
        meter_accounting: $event.data.row,
      })
    );
  };
  const act_1803307708030275_script_p2jxl32ur_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1803307708030275_script_p2jxl32ur_value_callable($event);
      },
    };
  };
  const act_1803307708030275_emit_1b67nojyw_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_1803307708030275_script_32ixxxl2p_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line_meter_accounting').delete($event.data.row)
    );
  };
  const act_1803307708030275_script_32ixxxl2p_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1803307708030275_script_32ixxxl2p_value_callable($event);
      },
    };
  };
  const act_1803307708030275_emit_ug4gh14qb_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7700024326226090_script_4949luvzw_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('invoice_line_accounting')
        .upsert($event.data.row)
        .then((p) => (emit('reload'), p))
    );
  };
  const act_7700024326226090_script_4949luvzw_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7700024326226090_script_4949luvzw_value_callable($event);
      },
    };
  };
  const act_7700024326226090_script_tf25p2d1f_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line_accounting').delete($event.data.row)
    );
  };
  const act_7700024326226090_script_tf25p2d1f_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7700024326226090_script_tf25p2d1f_value_callable($event);
      },
    };
  };
  const act_7700024326226090_emit_w6p5pvjvr_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7700024326226090_emit_2p0447ki6_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_1889980785424776_showModal_g0f01uaf5 =
    new actionTypes.ShowModalAction({
      actionArgs: act_1889980785424776_showModal_g0f01uaf5_getActionArgs,
    });
  const inst_evh_1889980785424776_clickPreventStop_kmwpx54km =
    new actionTypes.EventHandlerImpl({
      action: act_1889980785424776_showModal_g0f01uaf5,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_1889980785424776_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1889980785424776_clickPreventStop_kmwpx54km],
      event: 'click.prevent.stop',
    });
  const act_4892266742011185_setData_vaulrp05u = new actionTypes.SetDataAction({
    actionArgs: act_4892266742011185_setData_vaulrp05u_getActionArgs,
  });
  const inst_evh_4892266742011185_success_f48xw2q50 =
    new actionTypes.EventHandlerImpl({
      action: act_4892266742011185_setData_vaulrp05u,
      event: 'success',
      displayName: 'setData',
    });
  const act_4892266742011185_emit_4s37pgv56 = new actionTypes.EmitAction({
    actionArgs: act_4892266742011185_emit_4s37pgv56_getActionArgs,
  });
  const inst_evh_4892266742011185_success_76cujvxcz =
    new actionTypes.EventHandlerImpl({
      action: act_4892266742011185_emit_4s37pgv56,
      event: 'success',
      displayName: 'emit',
    });
  const act_4892266742011185_request_omq5308ko = new actionTypes.RequestAction({
    actionArgs: act_4892266742011185_request_omq5308ko_getActionArgs,
    displayName: 'deleteSelectedLines',
    events: [
      inst_evh_4892266742011185_success_f48xw2q50,
      inst_evh_4892266742011185_success_76cujvxcz,
    ],
  });
  const inst_evh_4892266742011185_clickPreventStop_h3zmqay0b =
    new actionTypes.EventHandlerImpl({
      action: act_4892266742011185_request_omq5308ko,
      event: 'click.prevent.stop',
      displayName: 'deleteSelectedLines',
    });
  const inst_evh_4892266742011185_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4892266742011185_clickPreventStop_h3zmqay0b],
      event: 'click.prevent.stop',
    });
  const act_5962862420439144_emit_j6peei6yy = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_j6peei6yy_getActionArgs,
  });
  const inst_evh_5962862420439144_saveCompleted_arl4mc9zv =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_j6peei6yy,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_saveCompleted_arl4mc9zv],
      event: 'save-completed',
    });
  const act_5962862420439144_script_un1t1gnlh = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_un1t1gnlh_getActionArgs,
  });
  const inst_evh_5962862420439144_save_7q4jj84ti =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_un1t1gnlh,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_save_7q4jj84ti],
    event: 'save',
  });
  const act_5962862420439144_script_upqx6xfy7 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_upqx6xfy7_getActionArgs,
  });
  const inst_evh_5962862420439144_close_gqybqiaf2 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_upqx6xfy7,
      event: 'close',
      displayName: 'script',
    });
  const act_5962862420439144_showModal_vhqp2tn3u =
    new actionTypes.ShowModalAction({
      actionArgs: act_5962862420439144_showModal_vhqp2tn3u_getActionArgs,
      events: [inst_evh_5962862420439144_close_gqybqiaf2],
    });
  const inst_evh_5962862420439144_createProduct_99ytpfa5e =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_showModal_vhqp2tn3u,
      event: 'createProduct',
      displayName: 'showModal',
    });
  const inst_evh_5962862420439144_createProduct =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_createProduct_99ytpfa5e],
      event: 'createProduct',
    });
  const act_5962862420439144_script_sfkp4ri0t = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_sfkp4ri0t_getActionArgs,
  });
  const inst_evh_5962862420439144_delete_noe1ytmsn =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_sfkp4ri0t,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_delete_noe1ytmsn],
    event: 'delete',
  });
  const act_5962862420439144_script_2yeb6ntrk = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_2yeb6ntrk_getActionArgs,
  });
  const inst_evh_5962862420439144_productSelected_nbmo2e9w2 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_2yeb6ntrk,
      event: 'product:selected',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_productSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_productSelected_nbmo2e9w2],
      event: 'product:selected',
    });
  const act_5962862420439144_script_gek3l0sww = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_gek3l0sww_getActionArgs,
  });
  const inst_evh_5962862420439144_quantityChange_uyov354wh =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_gek3l0sww,
      event: 'quantity:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_quantityChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_quantityChange_uyov354wh],
      event: 'quantity:change',
    });
  const act_5962862420439144_script_ekae4xjup = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_ekae4xjup_getActionArgs,
  });
  const inst_evh_5962862420439144_priceChange_sok600crm =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_ekae4xjup,
      event: 'price:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_priceChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_priceChange_sok600crm],
      event: 'price:change',
    });
  const act_5962862420439144_script_2oaevpjo8 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_2oaevpjo8_getActionArgs,
  });
  const inst_evh_5962862420439144_discountChange_v5v7ycq5o =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_2oaevpjo8,
      event: 'discount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_discountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_discountChange_v5v7ycq5o],
      event: 'discount:change',
    });
  const act_5962862420439144_script_tmrccs4jr = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_tmrccs4jr_getActionArgs,
  });
  const inst_evh_5962862420439144_netAmountChange_yt0y1165s =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_tmrccs4jr,
      event: 'net_amount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_netAmountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_netAmountChange_yt0y1165s],
      event: 'net_amount:change',
    });
  const act_5962862420439144_script_2r25ctv83 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_2r25ctv83_getActionArgs,
  });
  const inst_evh_5962862420439144_amountChange_1lou5vilx =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_2r25ctv83,
      event: 'amount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_amountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_amountChange_1lou5vilx],
      event: 'amount:change',
    });
  const act_5962862420439144_script_xdypq8nsb = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_xdypq8nsb_getActionArgs,
  });
  const inst_evh_5962862420439144_taxCodeChange_2tex90v0t =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_xdypq8nsb,
      event: 'tax_code:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_taxCodeChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_taxCodeChange_2tex90v0t],
      event: 'tax_code:change',
    });
  const act_5962862420439144_script_3bd42187a = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_3bd42187a_getActionArgs,
  });
  const inst_evh_5962862420439144_rentalOrPropertySelected_4b5fyx3ui =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_3bd42187a,
      event: 'rental_or_property:selected',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_rentalOrPropertySelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_rentalOrPropertySelected_4b5fyx3ui],
      event: 'rental_or_property:selected',
    });
  const act_5962862420439144_setData_vrhob4hv5 = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_vrhob4hv5_getActionArgs,
  });
  const inst_evh_5962862420439144_selectedRowsChanged_uvs2vuxqv =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_vrhob4hv5,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_5962862420439144_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_selectedRowsChanged_uvs2vuxqv],
      event: 'selectedRowsChanged',
    });
  const act_7789867021953041_emit_wipqptb3c = new actionTypes.EmitAction({
    actionArgs: act_7789867021953041_emit_wipqptb3c_getActionArgs,
  });
  const inst_evh_7789867021953041_reload_815xa6dcc =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_emit_wipqptb3c,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_7789867021953041_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7789867021953041_reload_815xa6dcc],
    event: 'reload',
  });
  const act_309515315410323_emit_6yrbsdxiu = new actionTypes.EmitAction({
    actionArgs: act_309515315410323_emit_6yrbsdxiu_getActionArgs,
  });
  const inst_evh_309515315410323_close_5umqs2v3j =
    new actionTypes.EventHandlerImpl({
      action: act_309515315410323_emit_6yrbsdxiu,
      event: 'close',
      when: inst_evh_309515315410323_close_5umqs2v3j_getWhen,
      displayName: 'emit',
    });
  const act_309515315410323_showModal_51mcfaqtg =
    new actionTypes.ShowModalAction({
      actionArgs: act_309515315410323_showModal_51mcfaqtg_getActionArgs,
      events: [inst_evh_309515315410323_close_5umqs2v3j],
    });
  const inst_evh_309515315410323_clickPreventStop_homb3cd6g =
    new actionTypes.EventHandlerImpl({
      action: act_309515315410323_showModal_51mcfaqtg,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_309515315410323_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_309515315410323_clickPreventStop_homb3cd6g],
      event: 'click.prevent.stop',
    });
  const act_880971572885910_script_7n6fdlyut = new actionTypes.ScriptAction({
    actionArgs: act_880971572885910_script_7n6fdlyut_getActionArgs,
  });
  const inst_evh_880971572885910_close_z3wdiomu5 =
    new actionTypes.EventHandlerImpl({
      action: act_880971572885910_script_7n6fdlyut,
      event: 'close',
      when: inst_evh_880971572885910_close_z3wdiomu5_getWhen,
      displayName: 'script',
    });
  const act_880971572885910_showModal_h8gcxm1wh =
    new actionTypes.ShowModalAction({
      actionArgs: act_880971572885910_showModal_h8gcxm1wh_getActionArgs,
      events: [inst_evh_880971572885910_close_z3wdiomu5],
    });
  const inst_evh_880971572885910_clickPreventStop_pmmc5n0i7 =
    new actionTypes.EventHandlerImpl({
      action: act_880971572885910_showModal_h8gcxm1wh,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_880971572885910_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_880971572885910_clickPreventStop_pmmc5n0i7],
      event: 'click.prevent.stop',
    });
  const act_1803307708030275_script_p2jxl32ur = new actionTypes.ScriptAction({
    actionArgs: act_1803307708030275_script_p2jxl32ur_getActionArgs,
  });
  const inst_evh_1803307708030275_save_yy72s9ein =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_script_p2jxl32ur,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_1803307708030275_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1803307708030275_save_yy72s9ein],
    event: 'save',
  });
  const act_1803307708030275_emit_1b67nojyw = new actionTypes.EmitAction({
    actionArgs: act_1803307708030275_emit_1b67nojyw_getActionArgs,
  });
  const inst_evh_1803307708030275_saveCompleted_8svoheh22 =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_emit_1b67nojyw,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_1803307708030275_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1803307708030275_saveCompleted_8svoheh22],
      event: 'save-completed',
    });
  const act_1803307708030275_script_32ixxxl2p = new actionTypes.ScriptAction({
    actionArgs: act_1803307708030275_script_32ixxxl2p_getActionArgs,
  });
  const inst_evh_1803307708030275_delete_ym5fooqt4 =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_script_32ixxxl2p,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_1803307708030275_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1803307708030275_delete_ym5fooqt4],
    event: 'delete',
  });
  const act_1803307708030275_emit_ug4gh14qb = new actionTypes.EmitAction({
    actionArgs: act_1803307708030275_emit_ug4gh14qb_getActionArgs,
  });
  const inst_evh_1803307708030275_deleteCompleted_zl0fbcxzo =
    new actionTypes.EventHandlerImpl({
      action: act_1803307708030275_emit_ug4gh14qb,
      event: 'delete-completed',
      displayName: 'emit',
    });
  const inst_evh_1803307708030275_deleteCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1803307708030275_deleteCompleted_zl0fbcxzo],
      event: 'delete-completed',
    });
  const act_7700024326226090_script_4949luvzw = new actionTypes.ScriptAction({
    actionArgs: act_7700024326226090_script_4949luvzw_getActionArgs,
  });
  const inst_evh_7700024326226090_save_7cfw5lgex =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_script_4949luvzw,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_7700024326226090_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7700024326226090_save_7cfw5lgex],
    event: 'save',
  });
  const act_7700024326226090_script_tf25p2d1f = new actionTypes.ScriptAction({
    actionArgs: act_7700024326226090_script_tf25p2d1f_getActionArgs,
  });
  const inst_evh_7700024326226090_delete_zzt333zjn =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_script_tf25p2d1f,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_7700024326226090_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7700024326226090_delete_zzt333zjn],
    event: 'delete',
  });
  const act_7700024326226090_emit_w6p5pvjvr = new actionTypes.EmitAction({
    actionArgs: act_7700024326226090_emit_w6p5pvjvr_getActionArgs,
  });
  const inst_evh_7700024326226090_saveCompleted_4s2rqp7lk =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_emit_w6p5pvjvr,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_7700024326226090_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7700024326226090_saveCompleted_4s2rqp7lk],
      event: 'save-completed',
    });
  const act_7700024326226090_emit_2p0447ki6 = new actionTypes.EmitAction({
    actionArgs: act_7700024326226090_emit_2p0447ki6_getActionArgs,
  });
  const inst_evh_7700024326226090_deleteCompleted_enwnsj0al =
    new actionTypes.EventHandlerImpl({
      action: act_7700024326226090_emit_2p0447ki6,
      event: 'delete-completed',
      displayName: 'emit',
    });
  const inst_evh_7700024326226090_deleteCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7700024326226090_deleteCompleted_enwnsj0al],
      event: 'delete-completed',
    });

  const actions = {
    inst_evh_1889980785424776_clickPreventStop_kmwpx54km,
    inst_evh_1889980785424776_clickPreventStop,
    inst_evh_4892266742011185_success_f48xw2q50,
    inst_evh_4892266742011185_success_76cujvxcz,
    inst_evh_4892266742011185_clickPreventStop_h3zmqay0b,
    deleteSelectedLines: inst_evh_4892266742011185_clickPreventStop_h3zmqay0b,
    inst_evh_4892266742011185_clickPreventStop,
    inst_evh_5962862420439144_saveCompleted_arl4mc9zv,
    inst_evh_5962862420439144_saveCompleted,
    inst_evh_5962862420439144_save_7q4jj84ti,
    inst_evh_5962862420439144_save,
    inst_evh_5962862420439144_close_gqybqiaf2,
    inst_evh_5962862420439144_createProduct_99ytpfa5e,
    inst_evh_5962862420439144_createProduct,
    inst_evh_5962862420439144_delete_noe1ytmsn,
    inst_evh_5962862420439144_delete,
    inst_evh_5962862420439144_productSelected_nbmo2e9w2,
    inst_evh_5962862420439144_productSelected,
    inst_evh_5962862420439144_quantityChange_uyov354wh,
    inst_evh_5962862420439144_quantityChange,
    inst_evh_5962862420439144_priceChange_sok600crm,
    inst_evh_5962862420439144_priceChange,
    inst_evh_5962862420439144_discountChange_v5v7ycq5o,
    inst_evh_5962862420439144_discountChange,
    inst_evh_5962862420439144_netAmountChange_yt0y1165s,
    inst_evh_5962862420439144_netAmountChange,
    inst_evh_5962862420439144_amountChange_1lou5vilx,
    inst_evh_5962862420439144_amountChange,
    inst_evh_5962862420439144_taxCodeChange_2tex90v0t,
    inst_evh_5962862420439144_taxCodeChange,
    inst_evh_5962862420439144_rentalOrPropertySelected_4b5fyx3ui,
    inst_evh_5962862420439144_rentalOrPropertySelected,
    inst_evh_5962862420439144_selectedRowsChanged_uvs2vuxqv,
    inst_evh_5962862420439144_selectedRowsChanged,
    inst_evh_7789867021953041_reload_815xa6dcc,
    inst_evh_7789867021953041_reload,
    inst_evh_309515315410323_close_5umqs2v3j,
    inst_evh_309515315410323_clickPreventStop_homb3cd6g,
    inst_evh_309515315410323_clickPreventStop,
    inst_evh_880971572885910_close_z3wdiomu5,
    inst_evh_880971572885910_clickPreventStop_pmmc5n0i7,
    inst_evh_880971572885910_clickPreventStop,
    inst_evh_1803307708030275_save_yy72s9ein,
    inst_evh_1803307708030275_save,
    inst_evh_1803307708030275_saveCompleted_8svoheh22,
    inst_evh_1803307708030275_saveCompleted,
    inst_evh_1803307708030275_delete_ym5fooqt4,
    inst_evh_1803307708030275_delete,
    inst_evh_1803307708030275_deleteCompleted_zl0fbcxzo,
    inst_evh_1803307708030275_deleteCompleted,
    inst_evh_7700024326226090_save_7cfw5lgex,
    inst_evh_7700024326226090_save,
    inst_evh_7700024326226090_delete_zzt333zjn,
    inst_evh_7700024326226090_delete,
    inst_evh_7700024326226090_saveCompleted_4s2rqp7lk,
    inst_evh_7700024326226090_saveCompleted,
    inst_evh_7700024326226090_deleteCompleted_enwnsj0al,
    inst_evh_7700024326226090_deleteCompleted,
  };

  const evh_1889980785424776_clickPreventStop_kmwpx54km = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1889980785424776_clickPreventStop_kmwpx54km.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_success_f48xw2q50 = async (event, scope) => {
    return await actions.inst_evh_4892266742011185_success_f48xw2q50.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_success_76cujvxcz = async (event, scope) => {
    return await actions.inst_evh_4892266742011185_success_76cujvxcz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_clickPreventStop_h3zmqay0b = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4892266742011185_clickPreventStop_h3zmqay0b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892266742011185_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4892266742011185_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted_arl4mc9zv = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted_arl4mc9zv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save_7q4jj84ti = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save_7q4jj84ti.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_close_gqybqiaf2 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_close_gqybqiaf2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_createProduct_99ytpfa5e = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_createProduct_99ytpfa5e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_createProduct = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_createProduct.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete_noe1ytmsn = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete_noe1ytmsn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_productSelected_nbmo2e9w2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_productSelected_nbmo2e9w2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_productSelected = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_productSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_quantityChange_uyov354wh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_quantityChange_uyov354wh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_quantityChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_quantityChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_priceChange_sok600crm = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_priceChange_sok600crm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_priceChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_priceChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_discountChange_v5v7ycq5o = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_discountChange_v5v7ycq5o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_discountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_discountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_netAmountChange_yt0y1165s = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_netAmountChange_yt0y1165s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_netAmountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_netAmountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_amountChange_1lou5vilx = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_amountChange_1lou5vilx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_amountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_amountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_taxCodeChange_2tex90v0t = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_taxCodeChange_2tex90v0t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_taxCodeChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_taxCodeChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_rentalOrPropertySelected_4b5fyx3ui = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_rentalOrPropertySelected_4b5fyx3ui.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_rentalOrPropertySelected = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_rentalOrPropertySelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_selectedRowsChanged_uvs2vuxqv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_selectedRowsChanged_uvs2vuxqv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_reload_815xa6dcc = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_reload_815xa6dcc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_reload = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_close_5umqs2v3j = async (event, scope) => {
    return await actions.inst_evh_309515315410323_close_5umqs2v3j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_clickPreventStop_homb3cd6g = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_309515315410323_clickPreventStop_homb3cd6g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_309515315410323_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_880971572885910_close_z3wdiomu5 = async (event, scope) => {
    return await actions.inst_evh_880971572885910_close_z3wdiomu5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_880971572885910_clickPreventStop_pmmc5n0i7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_880971572885910_clickPreventStop_pmmc5n0i7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_880971572885910_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_880971572885910_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_save_yy72s9ein = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_save_yy72s9ein.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_save = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_saveCompleted_8svoheh22 = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_saveCompleted_8svoheh22.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_delete_ym5fooqt4 = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_delete_ym5fooqt4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_delete = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_deleteCompleted_zl0fbcxzo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1803307708030275_deleteCompleted_zl0fbcxzo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1803307708030275_deleteCompleted = async (event, scope) => {
    return await actions.inst_evh_1803307708030275_deleteCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_save_7cfw5lgex = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_save_7cfw5lgex.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_save = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_delete_zzt333zjn = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_delete_zzt333zjn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_delete = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_saveCompleted_4s2rqp7lk = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_saveCompleted_4s2rqp7lk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_deleteCompleted_enwnsj0al = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7700024326226090_deleteCompleted_enwnsj0al.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7700024326226090_deleteCompleted = async (event, scope) => {
    return await actions.inst_evh_7700024326226090_deleteCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" alignItems="stretch" v-if="loaded">
    <ItpBox alignItems="center" class="pb-2">
      <strong>
        {{ pgettext('Invoice', 'Invoice Lines') }}
      </strong>
      <strong class="ms-1" v-if="invoiceLines.length">
        ({{ invoiceLines.length }})
      </strong>
      <ItpButton
        :icon="$config.uploadIcon"
        size="sm"
        :text="pgettext('Invoice|InvoiceLines', 'Upload...')"
        :tooltip="
          pgettext(
            'Invoice|InvoiceLines|Tooltip',
            'Upload invoice lines from file.'
          )
        "
        class="ms-3"
        v-if="!invoice.isReadOnly"
        @click.prevent.stop="evh_1889980785424776_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        :icon="$config.deleteIcon"
        size="sm"
        :text="pgettext('Invoice|InvoiceLines|Command', 'Delete')"
        :tooltip="
          pgettext('Invoice|InvoiceLines|Tooltip', 'Remove the selected lines.')
        "
        :disabled="
          !hasSelectedInvoiceLines || actions.deleteSelectedLines.isRunning
        "
        :spinning="actions.deleteSelectedLines.isRunning"
        class="ms-3"
        v-if="!invoice.isReadOnly"
        @click.prevent.stop="evh_4892266742011185_clickPreventStop($event, {})"
      >
      </ItpButton>
    </ItpBox>
    <ItpDataTable
      rowKey="id"
      tableId="invoiceLines"
      rowDetailsMode="alwaysVisible"
      :rowHeader="b_5962862420439144_rowHeader"
      :paging="b_5962862420439144_paging"
      :sortable="b_5962862420439144_sortable"
      :loading="actions.deleteSelectedLines.isRunning"
      :editable="!invoice.isReadOnly"
      :value="sortedInvoiceLines"
      :groups="invoiceLineGroups"
      groupKeyField="line_group_id"
      variant="details"
      row-selection
      columnHeaderStyle="cellheaderWhenEditing"
      cellClass="fw-bold"
      footer-totals
      :rowTemplate="b_5962862420439144_rowTemplate"
      :messages="b_5962862420439144_messages"
      :globs="{ searchDataSource, messages }"
      :columns="b_5962862420439144_columns"
      class="h-100"
      @save-completed="evh_5962862420439144_saveCompleted($event, {})"
      @save="evh_5962862420439144_save($event, {})"
      @createProduct="evh_5962862420439144_createProduct($event, {})"
      @delete="evh_5962862420439144_delete($event, {})"
      @product:selected="evh_5962862420439144_productSelected($event, {})"
      @quantity:change="evh_5962862420439144_quantityChange($event, {})"
      @price:change="evh_5962862420439144_priceChange($event, {})"
      @discount:change="evh_5962862420439144_discountChange($event, {})"
      @net_amount:change="evh_5962862420439144_netAmountChange($event, {})"
      @amount:change="evh_5962862420439144_amountChange($event, {})"
      @tax_code:change="evh_5962862420439144_taxCodeChange($event, {})"
      @rental_or_property:selected="
        evh_5962862420439144_rentalOrPropertySelected($event, {})
      "
      @selectedRowsChanged="
        evh_5962862420439144_selectedRowsChanged($event, {})
      "
    >
      <template v-slot:rowGroupHeader="{ group, rows }">
        <ItpBox>
          <UcInvoiceLineGroup
            :invoice="invoice"
            :group="group"
            @reload="evh_7789867021953041_reload($event, { group, rows })"
          >
          </UcInvoiceLineGroup>
        </ItpBox>
      </template>
      <template v-slot:afterLastRow="{ table }">
        <ItpBox>
          <ItpLink
            :icon="$config.addIcon"
            size="sm"
            :text="pgettext('InvoiceLineGroup', 'Add group...')"
            class="ms-3"
            @click.prevent.stop="
              evh_309515315410323_clickPreventStop($event, { table })
            "
          >
          </ItpLink>
        </ItpBox>
      </template>
      <template v-slot:rowDetails="{ row }">
        <div class="container-fluid px-lg-1">
          <ItpRow class="gx-2">
            <ItpCol :w="2">
              <ItpText
                pre
                :text="row.data.notes"
                collapsible
                v-if="row.data.notes && !row.isEditing.value"
              >
              </ItpText>
              <ItpFormTextarea
                :rows="3"
                name="notes"
                :placeholder="gettext('Notes')"
                v-model="row.data.notes"
                :disabled="!row.isEditing.value"
                class="mb-2"
                v-if="row.isEditing.value"
              >
              </ItpFormTextarea>
              <ItpFormSearchableSelect
                name="reference_invoice_id"
                :placeholder="pgettext('InvoiceLine', 'Reference invoice')"
                v-model="row.data.reference_invoice_id"
                :initialOptions="
                  row.data.reference_invoice ? [row.data.reference_invoice] : []
                "
                :dataSource="b_1270943406604881_dataSource"
                valueField="id"
                clearable
                optionTemplate="<UcSuggestInvoiceOption :option=option></UcSuggestInvoiceOption>"
                use-option-template-for-selected-option
                v-if="row.isEditing.value"
              >
              </ItpFormSearchableSelect>
            </ItpCol>
            <ItpCol :w="10">
              <ItpBox
                v-if="
                  !row.isNew.value &&
                  (row.isEditing.value || row.data.meter_accounting) &&
                  invoiceRentalNumber
                "
              >
                <ItpLink
                  :icon="$config.addIcon"
                  size="sm"
                  :text="pgettext('InvoiceLines', 'Select readings...')"
                  @click.prevent.stop="
                    evh_880971572885910_clickPreventStop($event, { row })
                  "
                >
                </ItpLink>
              </ItpBox>
              <ItpDataTable
                tableId="invoiceLineMeters"
                variant="basic"
                compact
                :paging="b_1803307708030275_paging"
                :sortable="b_1803307708030275_sortable"
                :rowHeader="b_1803307708030275_rowHeader"
                use-index-as-row-key
                :fillEmptyRowSpace="b_1803307708030275_fillEmptyRowSpace"
                :value="
                  row.data.meter_accounting ? [row.data.meter_accounting] : []
                "
                :editable="!invoice.isReadOnly && !row.data.locked"
                :noCreateRow="!!row.data.meter_accounting"
                :globs="{ units, searchDataSource }"
                :messages="b_1803307708030275_messages"
                :rowTemplate="{
                  invoice_id: invoice.id,
                  invoice_line_id: row.data.id,
                  from_date: null,
                  from_amount: null,
                  from_unit: null,
                  to_date: null,
                  to_amount: null,
                  to_unit: null,
                  billing_multiplier: 1,
                }"
                :columns="b_1803307708030275_columns"
                class="bg-transparent datatable-basic mb-1"
                v-if="
                  (!row.isNew.value && row.isEditing.value) ||
                  row.data.meter_accounting
                "
                @save="evh_1803307708030275_save($event, { row })"
                @save-completed="
                  evh_1803307708030275_saveCompleted($event, { row })
                "
                @delete="evh_1803307708030275_delete($event, { row })"
                @delete-completed="
                  evh_1803307708030275_deleteCompleted($event, { row })
                "
              >
              </ItpDataTable>
              <ItpDataTable
                tableId="invoiceLineAccounting"
                variant="basic"
                compact
                :paging="b_7700024326226090_paging"
                :sortable="b_7700024326226090_sortable"
                :rowHeader="b_7700024326226090_rowHeader"
                :fillEmptyRowSpace="b_7700024326226090_fillEmptyRowSpace"
                :value="
                  row.data.accountings && row.data.accountings.data
                    ? row.data.accountings.data
                    : []
                "
                :editable="!invoice.isReadOnly && !row.data.locked"
                :rowTemplate="
                  schemaDefaults('CreateInvoiceLineAccounting', {
                    invoice_id: invoice.id,
                    invoice_line_id: row.data.id,
                  })
                "
                :globs="{ searchDataSource }"
                :messages="b_7700024326226090_messages"
                :columns="b_7700024326226090_columns"
                class="bg-transparent datatable-basic mb-1"
                v-if="
                  (row.isEditing.value && !row.isNew.value) ||
                  (row.data.accountings &&
                    row.data.accountings.data &&
                    row.data.accountings.data.length)
                "
                @save="evh_7700024326226090_save($event, { row })"
                @delete="evh_7700024326226090_delete($event, { row })"
                @save-completed="
                  evh_7700024326226090_saveCompleted($event, { row })
                "
                @delete-completed="
                  evh_7700024326226090_deleteCompleted($event, { row })
                "
              >
              </ItpDataTable>
              <ItpBox class="mb-3">
                <div class="ms-auto" v-if="hasQuantityMismatch(row)">
                  <ItpIcon
                    icon="material-symbols:warning-rounded"
                    class="text-warning"
                  >
                  </ItpIcon>
                  <ItpText
                    strong
                    :text="
                      pgettext('invoiceLineAccounting', 'Quantity mismatch.')
                    "
                  >
                  </ItpText>
                </div>
              </ItpBox>
            </ItpCol>
          </ItpRow>
        </div>
      </template>
    </ItpDataTable>
  </ItpBox>
</template>
